// DateRangePicker
// ================================================================================================
import _ from "lodash";
import $ from 'jquery';
import moment from 'moment';
import * as React from "react";

// dateranger picker js
import 'bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';

// internal library
import { DebugEx } from "@Utils/DebugEx";
import { isDefined, isFunction } from "@Utils/Micro";
import { DateRangePickerLocale, GetDateRangePickerLocalizationDictionaryDefaultValues } from './ReactBootstrapDateRangePickerLocalization';
import { Localization, LocalizationMapEnum } from './Localization/Localization';
// ------------------------------------------------------------------------------------------------
export type DateOrString = string | moment.Moment | Date;
export interface DateRangePickerOptions {
    /**
     * The start of the initially selected date range
     */
    startDate?: DateOrString;
    /**
     * The end of the initially selected date range
     */
    endDate?: DateOrString;
    /**
     * The earliest date a user may select
     */
    minDate?: DateOrString;
    /**
     * The latest date a user may select
     */
    maxDate?: DateOrString;
    /**
     * The maximum span between the selected start and end dates. Can have any property you can add to a moment object (i.e. days, months)
     */
    maxSpan?: moment.MomentInput | moment.Duration;
    /**
     * Show year and month select boxes above calendars to jump to a specific month and year
     */
    showDropdowns?: boolean;
    /**
     * The minimum year shown in the dropdowns when `showDropdowns` is set to true.
     */
    minYear?: number;
    /**
     * The maximum year shown in the dropdowns when `showDropdowns` is set to true.
     */
    maxYear?: number;
    /**
     * Show localized week numbers at the start of each week on the calendars
     */
    showWeekNumbers?: boolean;
    /**
     * Show ISO week numbers at the start of each week on the calendars
     */
    showISOWeekNumbers?: boolean;
    /**
     * Allow selection of dates with times, not just dates
     */
    timePicker?: boolean;
    /**
     * Increment of the minutes selection list for times (i.e. 30 to allow only selection of times ending in 0 or 30)
     */
    timePickerIncrement?: number;
    /**
     * Use 24- hour instead of 12- hour times, removing the AM/ PM selection.
     */
    timePicker24Hour?: boolean;
    /**
     * Show seconds in the timePicker.
     */
    timePickerSeconds?: boolean;
    /**
     * Set predefined date ranges the user can select from.Each key is the label for the range, and its value an array with two dates representing the bounds of the range.
     */
    ranges?: { [name: string]: [DateOrString, DateOrString] };
    /**
     * Whether to show the 'Custom Range' label or just pre-defined ranges
     */
    showCustomRangeLabel?: boolean;
    /**
     * Normally, if you use the `ranges` option to specify pre-defined date ranges, calendars
     * for choosing a custom date range are not shown until the user clicks "Custom Range".
     * When this option is set to true, the calendars for choosing a custom date range are always shown instead.
     */
    alwaysShowCalendars?: boolean;
    /**
     * Whether the picker appears aligned to the left, to the right, or centered under the HTML element it's attached to
     */
    opens?: 'left' | 'right' | 'center';
    /**
     * Whether the picker appears below (default) or above the HTML element it's attached to
     */
    drops?: 'down' | 'up';
    /**
     * CSS class names that will be added to all buttons in the picker
     */
    buttonClasses?: string[];
    /**
     * CSS class string that will be added to the apply button
     */
    applyButtonClasses?: string;
    /**
     * CSS class string that will be added to the cancel button
     */
    cancelButtonClasses?: string;
    /**
     * Allows you to provide localized strings for buttons and labels, customize the date display format, and change the first day of week for the calendars.
     */
    locale?: DateRangePickerLocale;
    /**
     * Show only a single calendar to choose one date, instead of a range picker with two calendars; the start and end dates provided to your callback will be the same single date chosen.
     */
    singleDatePicker?: boolean;
    /**
     * Hide the apply and cancel buttons, and automatically apply a new date range as soon as two dates or a predefined range is selected.
     */
    autoApply?: boolean;
    /**
     * When enabled, the two calendars displayed will always be for two sequential months (i.e.
     * January and February), and both will be advanced when clicking the left or right arrows
     * above the calendars.When disabled, the two calendars can be individually advanced and
     * display any month/ year.
     */
    linkedCalendars?: boolean;
    /**
     * A function that is passed each date in the two calendars before they are displayed, and may return true or false to indicate whether that date should be available for selection or not.
     */
    isInvalidDate?(startDate: DateOrString, endDate?: DateOrString): boolean;
    /**
     * A function that is passed each date in the two calendars before they are displayed, and may return a string or array of CSS class names to apply to that date's calendar cell.
     */
    isCustomDate?(date: DateOrString): string | string[] | undefined;
    /**
     * Indicates whether the date range picker should automatically update the value of an < input > element it's attached to at initialization and when the selected dates change.
     */
    autoUpdateInput?: boolean;
    /**
     * jQuery selector of the parent element that the date range picker will be added to, if not provided this will be 'body'
     */
    parentEl?: string;
}
// ------------------------------------------------------------------------------------------------
export interface EventHandler { (event?: any, picker?: any): any; }
// ------------------------------------------------------------------------------------------------
export interface DateRangePickerState { }
// ------------------------------------------------------------------------------------------------
export interface DateRangePickerProps extends DateRangePickerOptions {
    onShow?: EventHandler;
    onHide?: EventHandler;
    onShowCalendar?: EventHandler;
    onHideCalendar?: EventHandler;
    onApply?: EventHandler;
    onCancel?: EventHandler;
    onEvent?: EventHandler;
    containerStyles?: React.CSSProperties;
    containerClass?: string;
    /**
     * Whether input is enabled or disabled. If true, the picker won't popup.
     */
    disabled?: boolean;
    /**
     * Optional width. Default value: 200
     */
    containerWidth?: string | number;
    /**
     * Optinaly key for applying localization.
     */
    localizationKey?: string;
}
// ------------------------------------------------------------------------------------------------
export class DateRangePicker extends React.Component<DateRangePickerProps, DateRangePickerState> {
    // --------------------------------------------------------------------------------------------
    private _isVisible: boolean;
    // --------------------------------------------------------------------------------------------
    private static _handledEvents: string[] = ['Show', 'Hide', 'ShowCalendar', 'HideCalendar', 'Apply', 'Cancel'];
    // --------------------------------------------------------------------------------------------
    public _$picker: any = null;
    private _currentOptions: any;
    private static _options: string[] = [
        '<input>',
        'alwaysShowCalendars',
        'applyClass',
        'autoApply',
        'autoUpdateInput',
        'buttonClasses',
        'cancelClass',
        'dateLimit',
        'drops',
        'endDate',
        'isCustomDate',
        'isInvalidDate',
        'linkedCalendars',
        'locale',
        'maxDate',
        'minDate',
        'opens',
        'parentEl',
        'ranges',
        'showCustomRangeLabel',
        'showDropdowns',
        'showISOWeekNumbers',
        'showWeekNumbers',
        'singleDatePicker',
        'startDate',
        'template',
        'timePicker',
        'timePicker24Hour',
        'timePickerIncrement',
        'timePickerSeconds'
    ];
    // --------------------------------------------------------------------------------------------
    public static GetLocalizationDictionary() {
        const res = {};

        // fill map with DateRangePicker locale keys
        const locale = GetDateRangePickerLocalizationDictionaryDefaultValues();
        res['applyLabel'] = locale.applyLabel;
        res['cancelLabel'] = locale.cancelLabel;
        res['customRangeLabel'] = locale.customRangeLabel;

        // for each entry of the array `daysOfWeek`
        _.forEach(locale.daysOfWeek, (dayofweek, index) => {
            // create an key containing element's index
            res['daysOfWeek_' + index] = dayofweek;
        });

        // for each entry of the array `monthNames`
        _.forEach(locale.monthNames, (monthName, index) => {
            // create an key containing element's index
            res['monthNames_' + index] = monthName;
        });
        return res;
    }
    // --------------------------------------------------------------------------------------------
    private static _GetLocaleFromLocalizationInfo(props: DateRangePickerProps): DateRangePickerLocale {
        // get default values
        const res = GetDateRangePickerLocalizationDictionaryDefaultValues();

        // check for optional `localizationKey`
        if (!isDefined(props.localizationKey)) {
            // return default value
            return res;
        }

        // try get localization instance
        const localization = Localization.GetInstance(props.localizationKey)
        if (!isDefined(localization)) {
            DebugEx.TraceError("DateRangePicker: passed `localizationKey`: " + props.localizationKey + " but could find instance.");
            // fallback to defaults
            return res;
        }

        // instance found; try get text map
        const map = localization.GetTextMap(LocalizationMapEnum.DateRangePickerMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("DateRangePicker: passed `localizationKey`: " + props.localizationKey + " but could not get text map.");
            // fallback to defaults
            return res;
        }
        // ok! text map found! let's update response

        // try get value for `applyLabel`
        if (isDefined(map.applyLabel))
            res.applyLabel = map.applyLabel;

        // try get value for `cancelLabel`
        if (isDefined(map.cancelLabel))
            res.cancelLabel = map.cancelLabel;

        // try get value for `customRangeLabel`
        if (isDefined(map.customRangeLabel))
            res.customRangeLabel = map.customRangeLabel;

        // try get `daysOfWeek` and `monthNames` values from text map
        _.forOwn(map, (value, key) => {

            // is 'daysOfWeek_X` key?
            const daysOfWeekIndex = key.indexOf('daysOfWeek_');
            if (daysOfWeekIndex !== -1) {
                const array_index = parseInt(key.substring(daysOfWeekIndex + 'daysOfWeek_'.length));
                if (!isNaN(array_index)) {
                    try {
                        res.daysOfWeek[array_index] = map[key];
                    }
                    catch (ex) {
                        DebugEx.TraceCriticalException(ex);
                    }
                }
                return;
            }

            // is 'monthNames_X` key?
            const monthNamesIndex = key.indexOf('monthNames_');
            if (monthNamesIndex !== -1) {
                const array_index = parseInt(key.substring(monthNamesIndex + 'monthNames_'.length));
                if (!isNaN(array_index)) {
                    try {
                        res.monthNames[array_index] = map[key];
                    }
                    catch (ex) {
                        DebugEx.TraceCriticalException(ex);
                    }
                }
                return;
            }
        });

        return res;
    }
    // -------------------------------------------------------------------------------------------- --- Localization
    public static defaultProps: DateRangePickerProps = {
        containerClass: 'react-bootstrap-daterangepicker-container',
        containerStyles: {
            display: 'inline-block'
        },
        containerWidth: 200
    };
    // --------------------------------------------------------------------------------------------
    constructor(props: DateRangePickerProps) {
        super(props);
        this.state = {};
    }
    // --------------------------------------------------------------------------------------------
    get isFirefox(): boolean {
        return navigator.userAgent.indexOf('Firefox') > -1 ||
            (typeof (window as any).InstallTrigger !== 'undefined');
    }
    // --------------------------------------------------------------------------------------------
    componentDidMount() {
        if (this.props.disabled !== true)
            this._initPicker(this.props);
    }
    // --------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: DateRangePickerProps) {
        // check if nextProps disable the picker
        if (this.props.disabled === true) {
            this._destroyPicker();
            return;
        }
        // check if nextProps change the picker
        else if (this._isPickerInitialized()) {
            const nextOptions = this._getOptionsFromProps(this.props);
            const changedOptions = {};
            DateRangePicker._options.forEach(option => {
                if (this._currentOptions[option] !== nextOptions[option]) {
                    changedOptions[option] = nextOptions[option];
                }
            });
            this._setOptionsFromProps(changedOptions, this.props);
        }
        // check if nextProps enable the picker
        else
            this._initPicker(this.props);
    }
    // --------------------------------------------------------------------------------------------
    componentWillUnmount() {
        this._destroyPicker();
    }
    // --------------------------------------------------------------------------------------------
    private _handleScrollEvent = () => {
        if (this._isVisible) {
            // Recreate the picker.
            this._destroyPicker();
            this._initPicker();
        }
    }
    // --------------------------------------------------------------------------------------------
    private _makeEventHandler(eventType) {
        const { onEvent } = this.props;
        return (event, picker) => {
            if (isFunction(onEvent)) {
                onEvent(event, picker);
            }
            if (isFunction(this.props[eventType])) {
                this.props[eventType](event, picker);
            }
        };
    }
    // --------------------------------------------------------------------------------------------
    private _getOptionsFromProps(props: DateRangePickerProps) {
        props = props || this.props;
        let options = _.pick(props, DateRangePicker._options);

        // if not custom `locale` prop is not provided
        if (!isDefined(props.locale)) {
            // get value from other props' fields
            options.locale = DateRangePicker._GetLocaleFromLocalizationInfo(props);
        }
        return options || {};
    }
    // --------------------------------------------------------------------------------------------
    private _setOptionsFromProps(nextOptions, nextProps) {
        // detect `locale` change;
        if (!_.isEqual(this._currentOptions.locale, nextOptions.locale) ||
            this._currentOptions.timePicker != nextOptions.timePicker) {
            // Unfortunately the package used does not update its view properly, when `locale` is changed
            // We need to re-instantiate the component! 
            // 1. destroy picker
            this._destroyPicker();
            // 2. re-init picker
            this._initPicker(nextProps);
            return;
        }

        const keys = Object.keys(nextOptions);
        keys.forEach(key => {
            if (key === 'startDate') {
                this._$picker.data('daterangepicker').setStartDate(nextOptions[key]);
            } else if (key === 'endDate') {
                this._$picker.data('daterangepicker').setEndDate(nextOptions[key]);
            } else {
                this._$picker.data('daterangepicker')[key] = nextOptions[key];
            }
        });
    }
    // --------------------------------------------------------------------------------------------
    private _isPickerInitialized(): boolean {
        return isDefined(this._$picker) && this._$picker.data('daterangepicker');
    }
    // --------------------------------------------------------------------------------------------
    private _initPicker(props = this.props) {
        if (this._isPickerInitialized())
            return;

        this._currentOptions = this._getOptionsFromProps(props);

        // initialize
        this._$picker.daterangepicker(this._currentOptions);

        // attach event listeners
        DateRangePicker._handledEvents.forEach(
            event => {
                const lcase = event.toLowerCase();
                this._$picker.on(
                    lcase + '.daterangepicker',
                    this._makeEventHandler('on' + event)
                );
            }
        );

        this._$picker.on('show.daterangepicker', (ev, picker) => {
            // Start with default position.
            const props = picker.element.data('daterangepicker');
            props.drops = 'down';
            props.opens = 'right';

            // Reset mini picker.
            const el = picker.container[0] as HTMLElement;
            el.style.width = '';
            el.style.maxHeight = '';
            el.style.overflow = '';

            picker.updateView();
            picker.renderCalendar(props.opens);
            picker.showCalendars();

            let rect = el.getBoundingClientRect(),
                prevClass = '',
                nextClass = '';

            // Fits in viewport's width?
            if (window.innerWidth - rect.left - rect.width > 0) {
                props.opens = 'right';
                prevClass = 'opensleft';
                nextClass = 'opensright';
            }
            else {
                props.opens = 'left';
                prevClass = 'opensright';
                nextClass = 'opensleft';
            }

            // Add and remove the related classes.
            if (el.classList.contains(prevClass))
                el.classList.remove(prevClass);

            if (!el.classList.contains(nextClass))
                el.classList.add(nextClass);

            // Update and get size and viewport relation.
            picker.updateView();
            picker.renderCalendar(props.opens);
            picker.showCalendars();
            rect = el.getBoundingClientRect();

            const miniPicker = () => {
                el.style.width = 'min-content';
                el.style.maxHeight = '300px';
                el.style.overflow = 'auto';
            };

            // Fits in window's height?
            if (window.innerHeight < rect.height) {
                if (rect.top < (window.innerHeight / 2))
                    props.drops = 'down';
                else
                    props.drops = 'up';
                miniPicker();
            }
            else {
                // Fits in viewport's height?
                if (window.innerHeight - rect.top - rect.height > 0)
                    props.drops = 'down';
                else
                    props.drops = 'up';
            }

            // Fits in window's width?
            if (window.innerWidth < rect.width)
                miniPicker();

            // Update element's view.
            picker.updateView();
            this._isVisible = true;

            // Listen to scroll events.
            window.addEventListener('scroll', this._handleScrollEvent, !this.isFirefox);
        });

        this._$picker.on('hide.daterangepicker', (ev, picker) => {
            this._isVisible = false;

            // Don't listen to scroll events.
            window.removeEventListener('scroll', this._handleScrollEvent, !this.isFirefox);
        });
    }
    // --------------------------------------------------------------------------------------------
    private _destroyPicker() {
        if (isDefined(this._$picker) &&
            this._$picker.data('daterangepicker')) {
            // deattach event listeners
            DateRangePicker._handledEvents.forEach(
                event => {
                    const lcase = event.toLowerCase();
                    this._$picker.off(lcase + '.daterangepicker');
                }
            );
            this._$picker.data('daterangepicker').remove();
        }
    }
    // --------------------------------------------------------------------------------------------
    render() {
        // NOTE: 
        // Instead of wrapping children with a <div>, we use <button>. This is done to avoid ux problems introduced from 'bootstrap-daterangepicker'.
        // More specifically, the package checks if element in which is instantiated is a <button> or an <input>. If not it handles 'click' and 'keydown' events in an undesired manner.
        // It toggles show/hide state!
        const {
            disabled,
            children,
            containerWidth,
            containerClass,
            containerStyles } = this.props;
        return (
            <button
                ref={picker => this._$picker = $(picker)}
                className={containerClass}
                style={_.assign({},
                    // adding min-width due to width artifacts in Firefox
                    {
                        background: 'inherit',
                        border: '0px',
                        padding: '0px',
                        width: containerWidth,
                        minWidth: containerWidth
                    } as React.CSSProperties,
                    containerStyles)
                }
                disabled={disabled}>
                {children}
            </button>
        );
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------